import { titleCase } from '../titleCase.ts';
import { BUILDER_END } from './builder.ts';
import { holdersOf, isWithin, phasesClosed, type ProcessStep, type ProjectProcess } from './graph.ts';
import { PHASE_KEYS, earlierPhase, phaseOfNode, phasesOf, type PhaseKey } from './phases.ts';
import { downstream } from './schedule.ts';
import { startKey, type ProcessTreeDoc } from './schema.ts';

/**
 * Where a project's steps stand, read the same way wherever a step is drawn
 * (NSB's process page, the client's home): the phase each sits in, and its
 * state as a mark and a word. One reading, so a step never says one thing to
 * NSB and another to the client.
 */

const rank = (p: PhaseKey) => PHASE_KEYS.indexOf(p);

/**
 * Where a job is, as a phase (Jason, 2026-10-06: "funnel stage doesn't make sense because it should be driven by the
 * state of the project tree"; Still to decide, A): the earliest phase holding a step not done, the job's start left
 * out; once everything is done, the last phase it reached. Null while the job has nothing on it but its start.
 */
export function stagePhase(doc: ProcessTreeDoc, p: ProjectProcess): PhaseKey | null {
  const start = startKey(doc);
  const live = p.steps.filter((s) => !s.off && (s.key === null || s.key !== start));
  if (!live.length) return null;
  const phases = projectPhases(doc, p, null);
  const of = (ss: ProcessStep[]) => ss.map((s) => phases.get(s.id)).filter((x): x is PhaseKey => x !== undefined).map(rank);
  const open = of(live.filter((s) => s.state !== 'done'));
  const all = of(live);
  return open.length ? PHASE_KEYS[Math.min(...open)]! : all.length ? PHASE_KEYS[Math.max(...all)]! : null;
}

/**
 * The phase each live step sits in: its own where it has one (a job's own step
 * the phase it was added to; a step of the tree added late, the phase under way
 * it landed in: design study 21, land A), else the tree's for a step from it (a
 * task the tree puts in an earlier phase than its step's is done early there:
 * design study 31, slice 4); a task with neither sits in its step's, so it is
 * done early only where a phase is set on it; a step the job grew itself with
 * none falls where what it waits on falls, or, waiting on nothing, where the job
 * is now (the phase of the next stop, else the earliest phase with open work). A
 * step whose key the tree no longer has is read as the job's own. A task done
 * early in a phase that has closed on the job since (`phasesClosed`), and not
 * done by the day it closed, can no longer be done early: it is done in its
 * step's phase (the step at the top), whether it came on after the phase closed
 * or the phase closed without it; one done by then stays its early phase's.
 */
export function projectPhases(doc: ProcessTreeDoc, p: ProjectProcess, nextId: string | null): Map<string, PhaseKey> {
  const live = new Map(p.steps.filter((s) => !s.off).map((s) => [s.id, s]));
  const closed = phasesClosed(doc, p);
  /** The live step at the top a step is in: itself at the top. */
  const topOf = (id: string): string => {
    const path = new Set([id]);
    let top = id;
    for (let up = live.get(id)?.parentId ?? null; up !== null && live.has(up) && !path.has(up); up = live.get(up)?.parentId ?? null) {
      path.add(up);
      top = up;
    }
    return top;
  };
  const treePhase = (id: string): PhaseKey | null => {
    const key = live.get(id)?.key;
    return key ? phaseOfNode(doc, key) : null;
  };
  const open = [...live.values()]
    .filter((s) => s.state !== 'done')
    .map((s) => treePhase(s.id))
    .filter((x): x is PhaseKey => x !== null);
  const now: PhaseKey = (nextId ? treePhase(nextId) : null) ?? (open.length ? PHASE_KEYS[Math.min(...open.map(rank))]! : 'discovery');
  const phases = new Map<string, PhaseKey>();
  const phaseOf = (id: string, seen = new Set<string>()): PhaseKey => {
    const got = phases.get(id);
    if (got) return got;
    // A step's own phase wins: a job's own step keeps the phase it was added to (design study 12, own step A), a late
    // step the phase it landed in (design study 21, land A).
    let ph = live.get(id)?.phase ?? treePhase(id) ?? null;
    seen.add(id);
    // A task with no phase of its own, nor the tree's, is in its step's (design study 31, slice 4).
    const holder = live.get(id)?.parentId;
    if (ph === null && holder && holder !== id && live.has(holder) && !seen.has(holder)) ph = phaseOf(holder, seen);
    if (ph === null) {
      const needs = p.links.filter((l) => l.kind === 'requires' && l.stepId === id && live.has(l.needsId) && !seen.has(l.needsId)).map((l) => phaseOf(l.needsId, seen));
      ph = needs.length ? needs.reduce((a, b) => (rank(b) > rank(a) ? b : a)) : now;
    }
    // Done early in a phase closed since, not done by then: done in its step's phase (design study 31, slice 4).
    const shut = closed.get(ph);
    const s = live.get(id);
    if (shut !== undefined && s?.parentId && !(s.state === 'done' && s.doneOn !== null && s.doneOn <= shut)) {
      const top = topOf(id);
      const own = top === id ? undefined : (phases.get(top) ?? (seen.has(top) ? undefined : phaseOf(top, seen)));
      if (own !== undefined && rank(ph) < rank(own)) ph = own;
    }
    phases.set(id, ph);
    return ph;
  };
  for (const id of live.keys()) phaseOf(id);
  return phases;
}

/**
 * A gate holds its whole phase (spec item 7; Jason, 2026-10-07): the gate that closes a phase waits, by rule and with
 * no line drawn for it, on every step of its phase the job has that is not done, so it is not passed, approved or
 * reached until they are (a decision of the phase among them), and passing it before is a deviation. A step filed away
 * counts for nothing. What comes after the gate (waits on it, however far), what holds it or sits inside it, and the
 * tree's two ends are never its phase's to finish. For each gate on the job that closes a phase, done or not, the
 * steps it waits on by the rule, outermost first (a task is named only where the step holding it is done).
 */
export function phaseHolds(doc: ProcessTreeDoc, p: ProjectProcess): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const { gate, members } of phaseMembers(doc, p)) {
    const open = members.filter((s) => s.state !== 'done');
    const ids = new Set(open.map((s) => s.id));
    out.set(
      gate.id,
      open.filter((s) => !holdersOf(p, s.id).some((h) => ids.has(h))).map((s) => s.id),
    );
  }
  return out;
}

/**
 * Each gate on the job that closes a phase, with the steps of its phase it waits on by rule (item 7), whatever their
 * state: the job's live steps in its phase, but what comes after it, what holds it or sits inside it, and the tree's two
 * ends. The one reading `phaseHolds` and the holds (`gateHolds`) both take.
 */
function phaseMembers(doc: ProcessTreeDoc, p: ProjectProcess): { phase: PhaseKey; gate: ProcessStep; members: ProcessStep[] }[] {
  const phases = phasesOf(doc).filter((ph) => ph.closedBy);
  if (!phases.length) return [];
  const live = p.steps.filter((s) => !s.off);
  const phaseOf = projectPhases(doc, p, null);
  const start = startKey(doc);
  const out: { phase: PhaseKey; gate: ProcessStep; members: ProcessStep[] }[] = [];
  for (const ph of phases) {
    const gate = live.find((s) => s.key === ph.closedBy && s.instance === null && s.kind === 'gate');
    if (!gate) continue;
    // A task done early is its phase's, never after its gate for being its step's (design study 31, slice 4).
    const after = downstream(p, gate.id, phaseOf);
    const holders = new Set(holdersOf(p, gate.id));
    out.push({
      phase: ph.key,
      gate,
      members: live.filter(
        (s) =>
          s.id !== gate.id &&
          phaseOf.get(s.id) === ph.key &&
          !after.has(s.id) &&
          !holders.has(s.id) &&
          !isWithin(p, s.id, gate.id) &&
          (s.key === null || (s.key !== start && s.key !== BUILDER_END)),
      ),
    });
  }
  return out;
}

/** A gate or inspection not done (not passed) that holds a step back (L-17 B), and how far back it is. */
export interface Hold {
  id: string;
  /**
   * How far back it is along the lines: 1 when the step waits on it directly (or the step holding it does), 2 when on a
   * step that waits on it, and so on, a step's list read in its order around an inspection (L-25d). Null when only the
   * phases put it before the step: the gate that closes a phase holds every phase after it.
   */
  back: number | null;
}

/**
 * The order the holds read (Jason, 2026-10-08, L-17 B: "every gate and inspection holds everything after it"; L-25d):
 * the schedule's order of work (a step that holds steps a start before them and an end after them, each wait from the
 * end of what is waited on to the start of what waits), and, by rule, with no line drawn: an inspection in a step's list
 * after what is listed above it and before what is listed under it, the list's order being the order, the same for every
 * list; the gate that closes a phase after every step of its phase (item 7) and before every step of the phases after
 * it (the gate that opens a phase holds the whole phase, L-17 A, which comes with B). A rule never closes a loop: one
 * that would is left out (a job drawn by hand against it keeps its own order). The lines (the waits, and a list's order)
 * are told from what only the phases put in order, for how far back a hold is. A task done in an earlier phase than its
 * step (design study 31, slice 4) is before its step's end and never after its start, nor after anything of a later
 * phase in its list's order: what holds its step does not hold it, the gate that opens its own phase does (the
 * phases' rule, as for every step of that phase), and it is its phase's to finish before the gate that closes it.
 */
function holdOrder(doc: ProcessTreeDoc, p: ProjectProcess) {
  const live = p.steps.filter((s) => !s.off);
  const phaseOf = projectPhases(doc, p, null);
  /** Done in an earlier phase than `than`: a task done early, apart from its step's start (`earlierPhase`). */
  const earlier = (id: string, than: string) => earlierPhase(phaseOf, id, than);
  const byId = new Map(live.map((s) => [s.id, s]));
  const kids = new Map<string, ProcessStep[]>();
  for (const s of live) if (s.parentId && s.parentId !== s.id && byId.has(s.parentId)) kids.set(s.parentId, [...(kids.get(s.parentId) ?? []), s]);
  const group = (id: string) => (kids.get(id)?.length ?? 0) > 0;
  const entry = (id: string) => (group(id) ? `${id}:start` : id);
  const exit = (id: string) => (group(id) ? `${id}:end` : id);
  /** Each edge: where it goes, and its length along the lines (0 inside a step, 1 for a wait), or null for the phases' rule. */
  const succ = new Map<string, { to: string; line: 0 | 1 | null }[]>();
  const pred = new Map<string, string[]>();
  for (const s of live) {
    for (const n of group(s.id) ? [`${s.id}:start`, `${s.id}:end`] : [s.id]) {
      succ.set(n, []);
      pred.set(n, []);
    }
  }
  const add = (a: string, b: string, line: 0 | 1 | null) => {
    if (a === b) return;
    succ.get(a)!.push({ to: b, line });
    pred.get(b)!.push(a);
  };
  const walk = (from: string, next: (n: string) => readonly string[]): Set<string> => {
    const seen = new Set<string>();
    const stack = [from];
    while (stack.length) {
      const n = stack.pop()!;
      if (seen.has(n)) continue;
      seen.add(n);
      stack.push(...next(n));
    }
    return seen;
  };
  const forward = (from: string) => walk(from, (n) => (succ.get(n) ?? []).map((e) => e.to));
  const backward = (from: string) => walk(from, (n) => pred.get(n) ?? []);
  for (const [holder, ks] of kids) {
    for (const c of ks) {
      if (!earlier(c.id, holder)) add(`${holder}:start`, entry(c.id), 0);
      add(exit(c.id), `${holder}:end`, 0);
    }
  }
  for (const l of p.links) if (l.kind === 'requires' && byId.has(l.stepId) && byId.has(l.needsId)) add(exit(l.needsId), entry(l.stepId), 1);
  // A step's list, in its order, around an inspection in it (L-25d); never a task done in an earlier phase after one of
  // a later phase.
  for (const ks of kids.values()) {
    ks.forEach((s, i) => {
      if (s.kind !== 'inspection') return;
      for (const above of ks.slice(0, i)) if (!earlier(s.id, above.id) && !forward(entry(s.id)).has(exit(above.id))) add(exit(above.id), entry(s.id), 1);
      for (const under of ks.slice(i + 1)) if (!earlier(under.id, s.id) && !forward(entry(under.id)).has(exit(s.id))) add(exit(s.id), entry(under.id), 1);
    });
  }
  // The gate that closes a phase after its phase (item 7), then before the phases after it.
  const closers = phaseMembers(doc, p);
  for (const { gate, members } of closers) {
    const after = forward(entry(gate.id));
    for (const m of members) if (!after.has(exit(m.id))) add(exit(m.id), entry(gate.id), null);
  }
  for (const { phase, gate } of closers) {
    const before = backward(entry(gate.id));
    for (const s of live) {
      const ph = phaseOf.get(s.id);
      if (s.id === gate.id || !ph || rank(ph) <= rank(phase) || before.has(entry(s.id))) continue;
      add(exit(gate.id), entry(s.id), null);
    }
  }
  /** How far each point is from `from` along the lines alone (0-1 breadth first: inside a step costs nothing). */
  const along = (from: string): Map<string, number> => {
    const dist = new Map<string, number>([[from, 0]]);
    const queue: string[] = [from];
    while (queue.length) {
      const n = queue.shift()!;
      const d = dist.get(n)!;
      for (const e of succ.get(n) ?? []) {
        if (e.line === null) continue;
        const nd = d + e.line;
        if ((dist.get(e.to) ?? Infinity) <= nd) continue;
        dist.set(e.to, nd);
        if (e.line === 0) queue.unshift(e.to);
        else queue.push(e.to);
      }
    }
    return dist;
  };
  return { live, entry, exit, forward, backward, along };
}

const isHold = (s: Pick<ProcessStep, 'kind' | 'state' | 'off'>) => (s.kind === 'gate' || s.kind === 'inspection') && s.state !== 'done' && !s.off;

/**
 * What holds each step of a job back (Jason, 2026-10-08, L-17 B): every gate and every inspection not done holds
 * everything after it, through any number of steps, mid-phase gates like Permits in Hand too, in the order the holds read
 * (`holdOrder`: the waits, a step's list around an inspection, the phases' gates). For each live step not done, the
 * gates and inspections that hold it, nearest first: it does not go under way, and is not done, approved or decided,
 * until they are (the way past one is marking it done early, with a reason: a deviation the client confirms where it
 * closes a phase, an open exception otherwise). A step's own tasks are not its holds: it waits on them to be done (L-23).
 */
export function gateHolds(doc: ProcessTreeDoc, p: ProjectProcess): Map<string, Hold[]> {
  const out = new Map<string, Hold[]>();
  const holds = p.steps.filter(isHold);
  if (!holds.length) return out;
  const g = holdOrder(doc, p);
  const order = new Map(p.steps.map((s, i) => [s.id, i]));
  for (const h of holds) {
    const from = g.exit(h.id);
    const reached = g.forward(from);
    const dist = g.along(from);
    for (const s of g.live) {
      if (s.id === h.id || s.state === 'done' || !reached.has(g.entry(s.id))) continue;
      out.set(s.id, [...(out.get(s.id) ?? []), { id: h.id, back: dist.get(g.entry(s.id)) ?? null }]);
    }
  }
  for (const list of out.values()) list.sort((a, b) => (a.back ?? Infinity) - (b.back ?? Infinity) || order.get(a.id)! - order.get(b.id)!);
  return out;
}

/**
 * Everything before a step in the order the holds read (`holdOrder`), what is inside it and inside each of them
 * included: what "Done up to here" marks done, so a step settled is never past a gate, an inspection or a phase left not
 * done. The step itself first.
 */
export function everythingBefore(doc: ProcessTreeDoc, p: ProjectProcess, id: string): string[] {
  const g = holdOrder(doc, p);
  if (!g.live.some((s) => s.id === id)) return [];
  const seen = g.backward(g.exit(id));
  return [id, ...g.live.filter((s) => s.id !== id && seen.has(g.exit(s.id))).map((s) => s.id)];
}

/**
 * An inspection's place in its step's list (L-25d; Jason, 2026-10-08: the list's order is the order, the same for every
 * list): what is listed above it, in order, which it waits on as on a wait drawn to it. None for a step in no list.
 */
export function listAbove(p: ProjectProcess, id: string): ProcessStep[] {
  const s = p.steps.find((x) => x.id === id && !x.off);
  if (!s?.parentId || s.kind !== 'inspection') return [];
  const list = p.steps.filter((x) => !x.off && x.parentId === s.parentId && x.id !== s.parentId);
  return list.slice(0, list.findIndex((x) => x.id === id));
}

/**
 * A step's tasks not done (L-23; Jason, 2026-10-08: "the step should not be able to be marked done until all the sub
 * tasks are completed weather through marking them complete or doing the thing the subtask requires"): every live step
 * inside it, at any depth, that holds none of its own and is not done, in its list's order. A step is done only once
 * there are none, each done by being marked done or by what it requires (an inspection passed, a decision decided);
 * the client's stay theirs.
 */
export function openTasks(p: ProjectProcess, id: string): ProcessStep[] {
  const live = p.steps.filter((s) => !s.off);
  const holding = new Set(live.flatMap((s) => (s.parentId ? [s.parentId] : [])));
  return live.filter((s) => s.id !== id && s.state !== 'done' && !holding.has(s.id) && isWithin(p, s.id, id));
}

/**
 * A step as a hold or a refusal names it (L-17 B): its title, and for a task in a step's list the step it is in, so a
 * list's own "The Work" or "Inspection" says whose: "Inspection in Footings and Foundation". `nameOf` gives the reader's
 * name for a step (a client's, its client line where NSB wrote one); its title by default. Named beside a step of the
 * same list (`beside`), the list goes without saying.
 */
export function stepCalled(p: Pick<ProjectProcess, 'steps'>, id: string, nameOf: (s: ProcessStep) => string = (s) => s.title, beside?: string): string {
  const s = p.steps.find((x) => x.id === id);
  if (!s) return '';
  const holder = s.parentId ? p.steps.find((x) => x.id === s.parentId && !x.off) : undefined;
  const near = beside !== undefined && holder !== undefined && p.steps.find((x) => x.id === beside)?.parentId === holder.id;
  return holder && !near ? `${titleCase(nameOf(s))} in ${titleCase(nameOf(holder))}` : titleCase(nameOf(s));
}

const NUMBERS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const counted = (n: number, one: string, many: string) => `${n <= 10 ? NUMBERS[n] : n} ${n === 1 ? one : many}`;

/** How far back a hold is, in words: "two steps back"; none for a wait drawn to it, or a hold the phases alone make. */
export const stepsBack = (back: number | null): string | null => (back !== null && back > 1 ? `${counted(back, 'step', 'steps')} back` : null);

/**
 * A hold named, as every page and refusal names it (L-17 B): its title, how far back it is, and that it is not done
 * (a gate, Jason, 2026-10-06) or not passed (an inspection): "Construction Authorized, two steps back, is not done yet";
 * one only the phases put before it, by the phase it closes: "Preliminary Design Lock, which closes Design, is not done
 * yet".
 */
export function holdNamed(hold: { title: string; kind: string }, back: number | null, closes: string | null = null): string {
  const far = stepsBack(back) ?? (back === null && closes ? `which closes ${closes}` : null);
  return `${titleCase(hold.title)}${far ? `, ${far},` : ''} is not ${hold.kind === 'inspection' ? 'passed' : 'done'} yet`;
}

/**
 * What a step waits on of its tasks, in words (L-23, as design study 24 drew it: "Waiting on the client: 2 of their
 * tasks. Build Contract is done once they are."): the client's counted as theirs, NSB's and the rest named.
 */
export function tasksWaiting(title: string, open: readonly { title: string; client: boolean }[]): string {
  const theirs = open.filter((t) => t.client).length;
  const ours = open.filter((t) => !t.client);
  const named = ours.slice(0, 3).map((t) => titleCase(t.title));
  const list = ours.length > 3 ? `${named.join(', ')} and ${ours.length - 3} more` : named.length > 1 ? `${named.slice(0, -1).join(', ')} and ${named.at(-1)}` : (named[0] ?? '');
  const them = theirs === 1 ? 'one of their tasks' : `${theirs} of their tasks`;
  const what = !ours.length
    ? `Waiting on the client: ${them}`
    : !theirs
      ? `Waiting on ${ours.length === 1 ? 'one' : ours.length} of its tasks: ${list}`
      : `Waiting on ${list}, and on the client: ${them}`;
  return `${what}. ${titleCase(title)} is done once ${open.length === 1 ? 'it is' : 'they are'}.`;
}

/** Everything a step waits on is done (a step turned off holds nothing): it can start. */
export function isReady(p: ProjectProcess, id: string): boolean {
  const live = new Map(p.steps.filter((s) => !s.off).map((s) => [s.id, s]));
  return p.links.filter((l) => l.kind === 'requires' && l.stepId === id).every((l) => (live.get(l.needsId)?.state ?? 'done') === 'done');
}

/**
 * The work has reached a step: it is under way, or everything it and the steps holding it wait on is done. A step of
 * the client's waits on them from then (core `clientSteps`), and only then do they decide it, do it or approve it (the
 * decide route refuses one not reached, `not_ready`), so what they are shown and what they can do are one rule. A task
 * done in an earlier phase than a step holding it (design study 31, slice 4), as the job's `phases` read (core
 * `projectPhases`), waits on nothing that step waits on; left out, every task waits on what its steps wait on.
 */
export function isReached(p: ProjectProcess, id: string, phases: ReadonlyMap<string, PhaseKey> = new Map()): boolean {
  const s = p.steps.find((x) => x.id === id);
  if (!s || s.off) return false;
  return s.state === 'in_progress' || [id, ...holdersOf(p, id).filter((h) => !earlierPhase(phases, id, h))].every((h) => isReady(p, h));
}

export type StandingState = 'done' | 'now' | 'next' | 'ahead' | 'override';

/**
 * A step's state as a mark and a word: done (or done on an override that
 * is still an open exception), under way, next, or ahead; the words by its
 * kind (a gate done or not done yet, never "passed" or "closed": Jason,
 * 2026-10-06; a milestone reached, a decision decided or waiting, an
 * inspection passed, not yet, or did not pass). The next mark (`isNext`: the
 * gate or landmark the schedule comes to next) is next only once the work has
 * reached it (`ready`); before, it is ahead, greyed with the steps before it
 * (P-20, Jason, 2026-10-09, of the Engagement Proposal drawn Next on a client's
 * sheet while Discovery was not started: "why is this not greyed out?").
 */
export function stepStanding(
  s: Pick<ProcessStep, 'kind' | 'state'>,
  o: { exception: boolean; isNext: boolean; ready: boolean; drivesNext?: boolean },
): { state: StandingState; word: string } {
  // The next mark, reached: the work has come to it (P-20).
  const next = o.isNext && o.ready;
  const state: StandingState = s.state === 'done' ? (o.exception ? 'override' : 'done') : s.state === 'in_progress' ? 'now' : next || (o.drivesNext && o.ready) ? 'next' : 'ahead';
  const word =
    s.kind === 'gate'
      ? s.state === 'done'
        ? o.exception
          ? 'Done on an override'
          : 'Done'
        : next
          ? 'Next'
          : 'Not done yet'
      : s.kind === 'inspection'
      ? s.state === 'done'
        ? o.exception
          ? 'Passed on override'
          : 'Passed'
        : s.state === 'in_progress'
          ? 'Did not pass'
          : next
            ? 'Next'
            : 'Not yet'
      : s.kind === 'milestone'
        ? s.state === 'done'
          ? 'Reached'
          : next
            ? 'Next'
            : 'Ahead'
        : s.kind === 'decision'
          ? s.state === 'done'
            ? 'Decided'
            : 'Waiting'
          : s.state === 'done'
            ? 'Done'
            : s.state === 'in_progress'
              ? 'Under way'
              : o.ready
                ? 'Ready'
                : 'Not started';
  return { state, word };
}

/**
 * How much of a job's process is kept as it ended (Jason, 2026-10-08, L-19 A: "once a job is complete it's locked in,
 * we can worry about retroactive record correction later when it comes up"): completed, its shape (nothing added, filed
 * away, left out or redrawn, nothing set on a step for the job alone, whose job it is unchanged, its draft never
 * saved), while the steps it has still go on to done, its last phase through to the tree's end; archived, the whole of
 * it (nothing on it changes: no state, day, pass, estimate, decision, deviation or person). A cancelled job stays open
 * until it is archived: its close-out is done after the cancel. The status door's own archiving, which marks the tree's
 * end done (adding it where the job lacks it), is the one change past it. Null while the job runs.
 */
export type ProcessKept = 'shape' | 'whole';

export function processKept(status: string): ProcessKept | null {
  return status === 'archived' ? 'whole' : status === 'closed' ? 'shape' : null;
}

/**
 * Whether a job is closed to its client, as its status stands: cancelled (archived or not), or archived, kept as it
 * ended (L-19 A). Nothing waits on them or comes up for them then, by one rule their Waiting on you and its emails, their
 * Decisions page, their drawing (core `clientSteps`, `clientNow`) and NSB's word for where a step of theirs stands read.
 * A completed job is not: the steps it has still go on to done, Close out through to its end, theirs among them. A
 * cancelled job's process stays open to NSB until it is archived; it asks nothing more of its client.
 */
export function clientClosed(status: string): boolean {
  return status === 'cancelled' || status === 'archived';
}

/**
 * A kept job's process in words, as the setup page, a panel and every refusal say it: `page` over the job's process,
 * `panel` on a step opened, `refused` the API's answer to a change it does not take, `undone` its answer to a step done
 * taken back to not done (development report L-30; Jason, 2026-10-08: refused on a completed job, whichever way). Named
 * by the tree's own end and the phase it closes ("Close out through to Project Complete").
 */
export function keptWords(kept: ProcessKept, doc: Pick<ProcessTreeDoc, 'nodes' | 'phases'>): { page: string; panel: string; refused: string; undone: string } {
  if (kept === 'whole') {
    const refused = 'This job is archived: its process is kept as it ended, and nothing on it changes.';
    return {
      page: refused,
      panel: 'The job is archived: its process reads as it ended.',
      refused,
      undone: refused,
    };
  }
  const end = doc.nodes.find((n) => n.key === BUILDER_END);
  const phase = end ? phaseOfNode(doc, BUILDER_END) : null;
  const phaseName = phase ? (phasesOf(doc).find((p) => p.key === phase)?.name ?? null) : null;
  const through = end ? `, ${phaseName ? `${phaseName} through ` : 'through '}to ${titleCase(end.title)}` : '';
  const page = `This job is completed: its process is kept as it ended. Nothing is added, left out or redrawn; the steps it has still go on to done${through}.`;
  return { page, panel: page, refused: page, undone: 'This job is completed: a step done on it stays done.' };
}

/** A step's days as they move (L-22): the day it went under way and the day it was done, each a calendar day; null, as it was. */
export interface DayMove {
  id: string;
  startedOn: string | null;
  doneOn: string | null;
}

/** The live steps inside a step, at any depth, in the job's order. */
function inside(p: ProjectProcess, id: string): ProcessStep[] {
  return p.steps.filter((s) => !s.off && s.id !== id && isWithin(p, s.id, id));
}

/**
 * A step's day set after the fact carries to the tasks done with it (Jason, 2026-10-08, L-22 A): its done day set by its
 * pencil, or the day it was marked done, passed or done up to here when that is a day gone by. A task done with it (done
 * on the day the step was done before, or after the day it is done now, which cannot be) takes the step's done day, and
 * its start never comes after it; a task with a day of its own keeps it (done earlier, by its own pencil or its own
 * click). A gate or an inspection inside it is passed on its own and keeps its day: one passed after the step's new day
 * is `refused`, since the step is done only once it is (L-23). `from` is the step's done day before, null when it was not
 * done; `to` its done day now.
 */
export function carriedDays(p: ProjectProcess, id: string, from: string | null, to: string): { moved: DayMove[]; refused: ProcessStep | null } {
  const moved: DayMove[] = [];
  let refused: ProcessStep | null = null;
  for (const t of inside(p, id)) {
    if (t.state !== 'done' || !t.doneOn) continue;
    const withIt = t.doneOn > to || (from !== null && from !== to && t.doneOn === from);
    if (!withIt) continue;
    if (t.kind === 'gate' || t.kind === 'inspection') {
      if (t.doneOn > to) refused ??= t;
      continue;
    }
    const startedOn = !t.startedOn || t.startedOn > to || (from !== null && t.startedOn === from) ? to : t.startedOn;
    moved.push({ id: t.id, startedOn, doneOn: to });
  }
  return { moved, refused };
}

/**
 * Never a start later than what is inside it (L-22 A: "a task done on a past day moves its step's start back to it"):
 * each step holding one of `ids`, at any depth, that has gone under way or is done, its start moved back to the
 * earliest day anything inside it went under way or was done, where that is earlier. Read on the job as it now stands;
 * `phases` as `firstInside` reads them: a task done early never moves its step's start.
 */
export function startsBack(p: ProjectProcess, ids: readonly string[], phases?: ReadonlyMap<string, PhaseKey>): DayMove[] {
  const byId = new Map(p.steps.map((s) => [s.id, s]));
  const holders = [...new Set(ids.flatMap((id) => holdersOf(p, id)))];
  const out: DayMove[] = [];
  for (const h of holders) {
    const s = byId.get(h);
    if (!s || s.off || (s.state !== 'in_progress' && s.state !== 'done')) continue;
    const earliest = firstInside(p, h, phases);
    if (!earliest || (s.startedOn && s.startedOn <= earliest.day)) continue;
    out.push({ id: s.id, startedOn: earliest.day, doneOn: null });
  }
  return out;
}

/**
 * A step done with its last task, the other way round (L-22 A: a day set after the fact carries to what was done with
 * it): a task's done day moved back by its pencil takes back with it the done day of each step holding it that was done
 * on the task's day before (`from`, done with it, as a step is done by its tasks), to the last day anything inside it was
 * done, where that is earlier. Nearest first, so a step holding steps follows them. Read on the job as it now stands.
 */
export function donesBack(p: ProjectProcess, ids: readonly string[], from: string): DayMove[] {
  const steps = new Map(p.steps.map((s) => [s.id, s]));
  const now = (): ProjectProcess => ({ ...p, steps: [...steps.values()] });
  const out = new Map<string, DayMove>();
  for (const id of ids) {
    for (const h of holdersOf(p, id)) {
      const s = steps.get(h);
      if (!s || s.off || s.state !== 'done' || s.doneOn !== from) break;
      const last = inside(now(), h).reduce<string | null>((d, t) => (t.state === 'done' && t.doneOn && (!d || t.doneOn > d) ? t.doneOn : d), null);
      if (!last || last >= from) break;
      steps.set(h, { ...s, doneOn: last });
      out.set(h, { id: h, startedOn: null, doneOn: last });
    }
  }
  return [...out.values()];
}

/**
 * Project Setup's days follow the job (development report L-30; Jason, 2026-10-08: on a job brought in with Done up to
 * here back to 2021, Project Setup read reached on the day the job was made here; "its day follows the earliest day set
 * on the job"): the job's start went under way and was done on the earliest day any other live step of the job went
 * under way or was done, never later than the day it was made (`made`), so a day set after the fact earlier than it,
 * by Done up to here, a pencil, a pass or a mark on a day gone by, takes it back with it. The move, or null when its days
 * are that day already, it is not done, or the job has no start.
 */
export function setupDays(
  steps: readonly Pick<ProcessStep, 'id' | 'key' | 'instance' | 'state' | 'off' | 'startedOn' | 'doneOn'>[],
  start: string | null,
  made: string,
): DayMove | null {
  const s = start ? steps.find((x) => x.key === start && x.instance === null && !x.off) : undefined;
  if (!s || s.state !== 'done') return null;
  let day = made;
  for (const t of steps) {
    if (t.off || t.id === s.id) continue;
    for (const d of [t.startedOn, t.doneOn]) if (d && d < day) day = d;
  }
  return s.startedOn === day && s.doneOn === day ? null : { id: s.id, startedOn: day, doneOn: day };
}

/**
 * The first thing inside a step to go under way or be done, and its day (L-22 A): a step went under way no later than
 * that. None while nothing inside it has. A task done in an earlier phase than the step (design study 31, slice 4), as
 * the job's `phases` read (core `projectPhases`), is not its step going under way, and is passed over; left out, every
 * task counts.
 */
export function firstInside(p: ProjectProcess, id: string, phases: ReadonlyMap<string, PhaseKey> = new Map()): { step: ProcessStep; day: string } | null {
  let out: { step: ProcessStep; day: string } | null = null;
  for (const t of inside(p, id)) {
    if (earlierPhase(phases, t.id, id)) continue;
    const day = t.startedOn ?? t.doneOn;
    if (day && (!out || day < out.day)) out = { step: t, day };
  }
  return out;
}
